import { App, Button, Checkbox, Flex, Typography, Upload } from 'antd';
import type { AttachmentDto } from '@/entities/attachment';
import { useDeleteAttachmentMutation, useLazyDownloadAttachmentQuery, useListAttachmentsQuery, useUpdateAttachmentMutation, useUploadAttachmentMutation } from '@/entities/attachment';
import { translateApiError } from '@/shared/api/errorToMessage';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export interface AttachmentsBlockProps {
  /** §13 «в несохранённом документе блок отключён» — undefined до первого сохранения. */
  documentId?: string;
  readOnly?: boolean;
}

/** §13 [ПРЕДЛ], §9 [ПРЕДЛ]: вложения к документу — загрузка, «Видно клиенту», скачивание, удаление. */
export function AttachmentsBlock({ documentId, readOnly }: AttachmentsBlockProps) {
  const { message } = App.useApp();
  const { data: attachments = [] } = useListAttachmentsQuery(documentId ?? '', { skip: !documentId });
  const [upload, { isLoading: isUploading }] = useUploadAttachmentMutation();
  const [update] = useUpdateAttachmentMutation();
  const [remove] = useDeleteAttachmentMutation();
  const [download] = useLazyDownloadAttachmentQuery();

  if (!documentId) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Вложения будут доступны после сохранения документа
      </Typography.Text>
    );
  }

  async function handleUpload(file: File): Promise<boolean> {
    try {
      await upload({ documentId: documentId!, file }).unwrap();
    } catch (e) {
      message.error(translateApiError(e));
    }
    return false; // не даём antd Upload самому слать запрос — уже отправили через RTK Query
  }

  async function handleDownload(attachment: AttachmentDto) {
    try {
      const blob = await download(attachment.id).unwrap();
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = attachment.fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  async function handleDelete(id: string) {
    try {
      await remove({ id, documentId: documentId! }).unwrap();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  return (
    <Flex vertical gap={4}>
      <Typography.Text style={{ fontSize: 12 }} strong>
        Вложения
      </Typography.Text>
      {attachments.map((a) => (
        <Flex key={a.id} gap={8} align="center" wrap>
          <a onClick={() => void handleDownload(a)}>{a.fileName}</a>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {formatSize(a.sizeBytes)}
          </Typography.Text>
          <Checkbox
            checked={a.visibleToClient}
            disabled={readOnly}
            onChange={(e) => void update({ id: a.id, documentId, visibleToClient: e.target.checked }).unwrap().catch((err) => message.error(translateApiError(err)))}
          >
            Видно клиенту
          </Checkbox>
          {!readOnly ? (
            <Button size="small" danger onClick={() => void handleDelete(a.id)}>
              Удалить
            </Button>
          ) : null}
        </Flex>
      ))}
      {!readOnly ? (
        <Upload beforeUpload={(file) => handleUpload(file)} showUploadList={false} disabled={isUploading || attachments.length >= 10}>
          <Button size="small" loading={isUploading}>
            Добавить файл
          </Button>
        </Upload>
      ) : null}
    </Flex>
  );
}
